import { SubtenantDto } from '@lmt/shared-types';
import { SettingsSeparatorLine } from '@lmt/unicorn-shared/app/components/common/ui-kit/configuration/SettingsSeparatorLine';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { ScriptInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/ScriptInput';
import { ToggleButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/ToggleButton';
import { MultiSelectDropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/MultiSelectDropDownInput';
import {
  PatientListConfigurationFormComponentProps,
  defaultPatientListConfigurationData,
  useFilteredPatientStatesOptions,
  usePatientStatusesOptions,
  useSexTypeOptions,
} from '@lmt/unicorn-shared/app/components/patients/list/common/types';
import { DateRangeConfiguration } from '@lmt/unicorn-shared/app/components/patients/list/components/DateRangeConfiguration';
import { TagsFilter } from '@lmt/unicorn-shared/app/components/patients/list/components/TagsFilter';
import { useSubtenants } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import styles from './PatientListFilterSettings.module.scss';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';

export const PatientListFilterSettings: React.FC<
  PatientListConfigurationFormComponentProps
> = (props) => {
  const i18n = useTranslation();
  const i18nScoped = useScopedTranslation('patient_list_configuration.filter');
  const claims = useClaims();

  const { control, watch, register, formState } = props.formMethods;

  const subtenants: SubtenantDto[] = useSubtenants();
  const sexTypeOptions = useSexTypeOptions();
  const patientStatesOptions = useFilteredPatientStatesOptions();
  const patientStatusesOptions = usePatientStatusesOptions();

  const [
    isFilterDisplayed,
    hasFilterBySubtenants,
    hasFilterByTags,
    hasFilterBySexTypes,
    hasFilterByStatuses,
    hasFilterByStates,
    hasFilterByLastActivity,
    hasFilterByLastVisit,
    hasFilterBySqlFilter,
  ] = watch([
    'isFilterDisplayed',
    'hasFilterBySubtenants',
    'hasFilterByTags',
    'hasFilterBySexTypes',
    'hasFilterByStatuses',
    'hasFilterByStates',
    'hasFilterByLastActivity',
    'hasFilterByLastVisit',
    'hasFilterBySqlFilter',
  ]);
  useEffect(() => {
    props.formMethods.setValue(
      'hasFilterBySubtenants',
      isFilterDisplayed &&
        defaultPatientListConfigurationData.hasFilterBySubtenants,
    );
    props.formMethods.setValue(
      'hasFilterByTags',
      isFilterDisplayed && defaultPatientListConfigurationData.hasFilterByTags,
    );
    props.formMethods.setValue(
      'hasFilterBySexTypes',
      isFilterDisplayed &&
        defaultPatientListConfigurationData.hasFilterBySexTypes,
    );
    props.formMethods.setValue(
      'hasFilterByStatuses',
      isFilterDisplayed &&
        defaultPatientListConfigurationData.hasFilterByStatuses,
    );
    props.formMethods.setValue(
      'hasFilterByStates',
      isFilterDisplayed &&
        defaultPatientListConfigurationData.hasFilterByStates,
    );

    props.formMethods.setValue(
      'hasFilterByLastActivity',
      isFilterDisplayed &&
        defaultPatientListConfigurationData.hasFilterByLastActivity,
    );
    props.formMethods.setValue(
      'hasFilterByLastVisit',
      isFilterDisplayed &&
        defaultPatientListConfigurationData.hasFilterByLastVisit,
    );
    props.formMethods.setValue(
      'hasFilterBySqlFilter',
      isFilterDisplayed &&
        defaultPatientListConfigurationData.hasFilterBySqlFilter,
    );
  }, [isFilterDisplayed]);
  const canFilterByStates =
    claims.permissions.patientDeleteAndRestore ||
    claims.permissions.patientAnonymizeAndDeletePermanently;

  return (
    <>
      <div className={styles.field}>
        <ToggleButton
          {...register('isFilterDisplayed')}
          title={i18nScoped.t('fields.filter')}
        />
      </div>
      {isFilterDisplayed && (
        <div>
          <SettingsSeparatorLine />
          <div className={styles.field}>
            <ToggleButton
              {...register('hasFilterBySubtenants')}
              title={i18nScoped.t('fields.subtenants')}
            />
          </div>
          {hasFilterBySubtenants && (
            <Field
              title={i18nScoped.t('fields.subtenants')}
              className={styles.field}
            >
              <Controller
                control={control}
                name={'subtenants'}
                render={({ field: { onChange, value } }) => (
                  <div className={styles.input}>
                    <MultiSelectDropDownInput
                      options={subtenants}
                      idFunction={'id'}
                      getOptionLabel={'name'}
                      placeholder={i18n.t(
                        'patients.list.filter.control.select_subtenants',
                      )}
                      allSelectedLabel={i18n.t(
                        'patients.list.filter.control.all_subtenants',
                      )}
                      onValueChanged={(subtenants) => {
                        onChange(subtenants.map((s) => s.id));
                      }}
                      values={value}
                      headerTitle={i18nScoped.t('fields.subtenants')}
                      hasSearchFilter={true}
                    />
                  </div>
                )}
              />
            </Field>
          )}
          <SettingsSeparatorLine />
          <div className={styles.field}>
            <ToggleButton
              {...register('hasFilterByTags')}
              title={i18nScoped.t('fields.tags')}
            />
          </div>
          {hasFilterByTags && (
            <Field title={i18nScoped.t('fields.tags')} className={styles.field}>
              <Controller
                control={control}
                name={'tags'}
                render={({ field: { onChange, value } }) => (
                  <div className={styles.input}>
                    <TagsFilter selectedTagIds={value} onChange={onChange} />
                  </div>
                )}
              />
            </Field>
          )}
          <SettingsSeparatorLine />
          <div className={styles.field}>
            <ToggleButton
              {...register('hasFilterBySexTypes')}
              title={i18nScoped.t('fields.sex_types')}
            />
          </div>
          {hasFilterBySexTypes && (
            <Field
              title={i18nScoped.t('fields.sex_types')}
              className={styles.field}
            >
              <Controller
                control={control}
                name={'sexTypes'}
                render={({ field: { onChange, value } }) => (
                  <div className={styles.input}>
                    <MultiSelectDropDownInput
                      options={sexTypeOptions}
                      idFunction={'value'}
                      getOptionLabel={'label'}
                      placeholder={i18n.t(
                        'patients.list.filter.control.select_sex_types',
                      )}
                      allSelectedLabel={i18n.t(
                        'patients.list.filter.control.all_sex_types',
                      )}
                      onValueChanged={(sexTypes) => {
                        onChange(sexTypes.map((x) => x.value));
                      }}
                      values={value}
                      headerTitle={i18nScoped.t('fields.sex_types')}
                    />
                  </div>
                )}
              />
            </Field>
          )}
          <SettingsSeparatorLine />
          <div className={styles.field}>
            <ToggleButton
              {...register('hasFilterByStatuses')}
              title={i18nScoped.t('fields.statuses')}
            />
          </div>
          {hasFilterByStatuses && (
            <Field
              title={i18nScoped.t('fields.statuses')}
              className={styles.field}
            >
              <Controller
                control={control}
                name={'statuses'}
                render={({ field: { onChange, value } }) => (
                  <div className={styles.input}>
                    <MultiSelectDropDownInput
                      options={patientStatusesOptions}
                      idFunction={'value'}
                      getOptionLabel={'label'}
                      placeholder={i18n.t(
                        'patients.list.filter.control.select_statuses',
                      )}
                      allSelectedLabel={i18n.t(
                        'patients.list.filter.control.all_statuses',
                      )}
                      onValueChanged={(statuses) => {
                        onChange(statuses.map((x) => x.value));
                      }}
                      values={value}
                      headerTitle={i18nScoped.t('fields.statuses')}
                    />
                  </div>
                )}
              />
            </Field>
          )}
          <SettingsSeparatorLine />
          {canFilterByStates && (
            <>
              <div className={styles.field}>
                <ToggleButton
                  {...register('hasFilterByStates')}
                  title={i18nScoped.t('fields.states')}
                />
              </div>
              {hasFilterByStates && (
                <Field
                  title={i18nScoped.t('fields.states')}
                  className={styles.field}
                >
                  <Controller
                    control={control}
                    name={'states'}
                    render={({ field: { onChange, value } }) => (
                      <div className={styles.input}>
                        <MultiSelectDropDownInput
                          options={patientStatesOptions}
                          idFunction={'value'}
                          getOptionLabel={'label'}
                          placeholder={i18n.t(
                            'patients.list.filter.control.select_states',
                          )}
                          allSelectedLabel={i18n.t(
                            'patients.list.filter.control.all_states',
                          )}
                          onValueChanged={(states) => {
                            onChange(states.map((x) => x.value));
                          }}
                          values={value}
                          headerTitle={i18nScoped.t('fields.states')}
                        />
                      </div>
                    )}
                  />
                </Field>
              )}
            </>
          )}
          <SettingsSeparatorLine />
          <div className={styles.field}>
            <ToggleButton
              {...register('hasFilterByLastActivity')}
              title={i18nScoped.t('fields.last_activity')}
            />
          </div>
          {hasFilterByLastActivity && (
            <Field
              title={i18nScoped.t('fields.last_activity')}
              className={styles.field}
            >
              <Controller
                control={control}
                name={'lastActivity'}
                render={({ field: { onChange, value } }) => (
                  <div className={styles.input}>
                    <DateRangeConfiguration
                      value={value!}
                      onChange={(newValue) => {
                        onChange(newValue);
                      }}
                      dateError={
                        !!(formState.errors.lastActivity as any)?.absoluteMoment
                      }
                      rangeError={
                        !!(formState.errors.lastActivity as any)
                          ?.isRelativeToCurrentMoment ||
                        !!(formState.errors.lastActivity as any)?.rangeInSeconds
                      }
                    />
                  </div>
                )}
              />
            </Field>
          )}
          <SettingsSeparatorLine />
          <div className={styles.field}>
            <ToggleButton
              {...register('hasFilterByLastVisit')}
              title={i18nScoped.t('fields.last_visit')}
            />
          </div>
          {hasFilterByLastVisit && (
            <Field
              title={i18nScoped.t('fields.last_visit')}
              className={styles.field}
            >
              <Controller
                control={control}
                name={'lastVisit'}
                render={({ field: { onChange, value } }) => (
                  <div className={styles.input}>
                    <DateRangeConfiguration
                      value={value!}
                      onChange={(newValue) => {
                        onChange(newValue);
                      }}
                      dateError={
                        !!(formState.errors.lastVisit as any)?.absoluteMoment
                      }
                      rangeError={
                        !!(formState.errors.lastVisit as any)
                          ?.isRelativeToCurrentMoment ||
                        !!(formState.errors.lastVisit as any)?.rangeInSeconds
                      }
                    />
                  </div>
                )}
              />
            </Field>
          )}
          <SettingsSeparatorLine />
          <div className={styles.field}>
            <ToggleButton
              {...register('hasFilterBySqlFilter')}
              title={i18nScoped.t('fields.sql_filter')}
            />
          </div>
          {hasFilterBySqlFilter && (
            <Field
              title={i18nScoped.t('fields.sql_filter')}
              className={styles.field}
            >
              <Controller
                control={control}
                name={'sqlFilter'}
                render={({ field: { onChange, value } }) => (
                  <div className={styles.input}>
                    <ScriptInput
                      value={value}
                      onChange={onChange}
                      predefinedText={i18n.t(
                        'patients.list.filter.control.sql_filter_preset',
                      )}
                      error={!!formState.errors.sqlFilter}
                      shortErrorText={
                        !!formState.errors.sqlFilter
                          ? i18n.t(
                              'patients.list.filter.control.sql_filter_error',
                            )
                          : undefined
                      }
                      fullErrorText={formState.errors.sqlFilter?.message}
                    />
                  </div>
                )}
              />
            </Field>
          )}
          <SettingsSeparatorLine />
        </div>
      )}
    </>
  );
};
